import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import Alert from 'shared/atoms/Alert';
import Form from 'shared/atoms/Form';
import FormInput from 'shared/atoms/FormFields/FormInput';
import Dialog from 'shared/molecules/Dialog';
import theme from 'shared/themes/default';
import { DOMAIN_LOCALES, LOCALES, MAX_PLAYLIST } from 'shared/const';
import { useForm } from 'shared/utils';

import { usePlaylist } from '../../../context';

function CreateNewPlaylistDialog() {
  const { t } = useTranslation();
  const {
    dialogs: {
      editPlaylistId,
      onClosePlaylistFormHandler: onCloseHandler,
      onCreatePlaylist,
      updatePlaylistHandler,
      formStatus,
      submittingForm,
    },
    domain,
    playlists: { listOwnedPlaylists },
  } = usePlaylist();

  const onSave = editPlaylistId ? updatePlaylistHandler : onCreatePlaylist;

  const onSubmitHandler = (values) => {
    onSave({
      ...values,
      id: editPlaylistId,
    });
  };

  const {
    disableSubmit,
    errors,
    handleBlur,
    handleChange,
    handleSubmit,
    properties,
    reset,
    setDefaults,
    values,
  } = useForm({
    changesRequired: true,
    fields: {
      name: {
        localised: true,
        maxLength: MAX_PLAYLIST.NAME_LENGTH,
        required: true,
      },
      description: {
        localised: true,
        maxLength: MAX_PLAYLIST.DESCRIPTION_LENGTH,
        required: true,
      },
    },
    locales: LOCALES,
    onSubmit: onSubmitHandler,
    requiredLocales: [DOMAIN_LOCALES[domain][0]],
  });

  const actions = [
    {
      ariaLabel: editPlaylistId
        ? t('playlist.updatePlaylist')
        : t('playlist.createPlaylist'),
      disabled: disableSubmit,
      handler: handleSubmit,
      label: editPlaylistId
        ? t('playlist.updatePlaylist')
        : t('playlist.createPlaylist'),
    },
  ];

  useEffect(() => {
    if (editPlaylistId) {
      const playlist = listOwnedPlaylists.find(
        (playlist) => playlist.id === editPlaylistId
      );

      setDefaults({
        name: playlist.name,
        description: playlist.description,
      });
    } else {
      reset();
    }
  }, [editPlaylistId]);

  const sxInput = { mb: theme.spacing.m2 };

  const dialogTitle = editPlaylistId
    ? t('playlist.updatePlaylist')
    : t('playlist.newPlaylist');

  return (
    <Dialog
      open
      onClose={onCloseHandler}
      ariaLabel={dialogTitle}
      title={dialogTitle}
      actions={actions}
      loading={submittingForm}
      formStatus={formStatus}
    >
      {formStatus && (
        <Alert
          message={formStatus.message}
          severity={formStatus.success ? 'success' : 'error'}
          theme="light"
        />
      )}
      {!formStatus && (
        <Form onSubmit={handleSubmit}>
          <FormInput
            id="name"
            name="name"
            label={t('playlist.title')}
            errors={errors}
            locale={DOMAIN_LOCALES[domain][0]}
            handleBlur={handleBlur}
            handleChange={handleChange}
            properties={properties}
            sx={sxInput}
            showMaxLength
            theme="light"
            values={values}
          />
          <FormInput
            id="description"
            name="description"
            label={t('playlist.description')}
            errors={errors}
            handleBlur={handleBlur}
            handleChange={handleChange}
            locale={DOMAIN_LOCALES[domain][0]}
            maxRows={4}
            minRows={4}
            multiline
            properties={properties}
            sx={sxInput}
            showMaxLength
            theme="light"
            values={values}
          />
        </Form>
      )}
    </Dialog>
  );
}

export default CreateNewPlaylistDialog;
